/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  PieChart as Chart,
  Pie,
  Sector,
  Cell,
  Legend,
  Tooltip,
} from 'recharts';

const COLORS = [
  '#DB2829',
  '#FF8042',
  '#FFBB28',
  '#0088FE',
  '#00C49F',
  '#029357',
];
const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PieChart = ({
  data = [],
  width,
  height,
  dataKey,
  nameKey,
  filled,
  innerRadius,
  outerRadius,
  legendStyle,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const onPieEnter = (data, index) => {
    setActiveIndex(index);
  };

  const wrapperStyle = {
    top: 100,
    right: 50,
    lineHeight: '24px',
    ...legendStyle,
  };

  const renderActiveShape = props => {
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
        >{`${nameKey}: ${value}`}</text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
        >
          {`(Rate ${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

  const renderColorfulLegendText = (value, entry) => {
    return (
      <span style={{ fontWeight: 500, padding: '10px' }}>
        {value}
      </span>
    );
  };

  const showLegend =
    filled && data.length > 0 && data.some(({ total }) => total > 0);

  return (
    <Chart width={width} height={height} style={{ display: 'flex' }}>
      <Tooltip />
      {!filled && (
        <Pie
          activeIndex={activeIndex}
          data={data}
          cx={width / 2}
          cy={150}
          activeShape={renderActiveShape}
          innerRadius={60}
          onMouseEnter={onPieEnter}
          outerRadius={80}
          fill="#8884d8"
          nameKey={nameKey}
          dataKey={dataKey}
        />
      )}

      {filled && (
        <Pie
          dataKey={dataKey}
          isAnimationActive
          label={renderCustomizedLabel}
          labelLine={false}
          data={data}
          cx={width / 3}
          cy={height / 2}
          innerRadius={innerRadius ?? 0}
          outerRadius={outerRadius ?? 100}
          fill="#8884d8"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={COLORS[index % COLORS.length]}
              da
            />
          ))}
        </Pie>
      )}
      {(showLegend || true) && (
        <Legend
          width={200}
          wrapperStyle={wrapperStyle}
          layout="vertical"
          verticalAlign="middle"
          iconSize={10}
          padding={5}
          formatter={renderColorfulLegendText}
        />
      )}
    </Chart>
  );
};

renderCustomizedLabel.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  midAngle: PropTypes.number.isRequired,
  innerRadius: PropTypes.number.isRequired,
  outerRadius: PropTypes.number.isRequired,
  percent: PropTypes.number.isRequired,
};

PieChart.propTypes = {
  data: PropTypes.instanceOf(Array).isRequired,
  color: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  cx: PropTypes.number,
  cy: PropTypes.number,
  innerRadius: PropTypes.number,
  outerRadius: PropTypes.number,
  filled: PropTypes.bool,
  legendStyle: PropTypes.instanceOf(Object),
};

PieChart.defaultProps = {
  color: '#8884d8',
  width: 800,
  height: 400,
  cx: 300,
  cy: 200,
  innerRadius: undefined,
  outerRadius: undefined,
  filled: false,
  legendStyle: {},
};

export default PieChart;
